import React from "react";
import {
  Calendar,
  MapPin,
  Target,
  Banknote,
  FileText,
  Edit2,
  Trash2,
  X,
  Martini,
} from "lucide-react";
import { DartsEvent, OrderedDrinks, ParticipantPaymentFee } from "../../types";
import { drinksApi } from "../../api/drinks";
import { eventsApi } from "../../api/events";
import { UI_TEXT } from "../../constants/uiText";
import DrinkOrderModal from "./DrinkOrderModal";

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: DartsEvent | null;
  isAdmin: boolean;
  currentUserName?: string;
  onEdit: () => void;
  onDelete?: (eventId: string) => Promise<void>;
  onStartTournament?: (eventId: string) => Promise<boolean>;
  onEndTournament?: (eventId: string) => void;
  onEntry?: (eventId: string) => Promise<void>;
  onCancelEntry?: (eventId: string) => Promise<void>;
  onViewTournamentResult?: (event: DartsEvent) => void;
}

function normalizeFormatLabel(format?: string): string {
  if (!format) return "-";
  return format;
}

function normalizeRank(rank: string): string {
  return rank.replace(/\s+/g, "").replace("位", "").toLowerCase();
}

function getRankMedal(rank: string): string | null {
  const normalized = normalizeRank(rank);
  if (normalized === "1") return "🥇";
  if (normalized === "2") return "🥈";
  if (normalized === "3") return "🥉";
  return null;
}

function getRankAccentClass(rank: string): string {
  const normalized = normalizeRank(rank);
  if (normalized === "1") return "text-amber-600";
  if (normalized === "2") return "text-slate-500";
  if (normalized === "3") return "text-orange-700";
  return "text-slate-700";
}

const EventDetailModal: React.FC<EventDetailModalProps> = ({
  isOpen,
  onClose,
  event,
  isAdmin,
  currentUserName,
  onEdit,
  onDelete,
  onStartTournament,
  onEndTournament,
  onEntry,
  onCancelEntry,
  onViewTournamentResult,
}) => {
  const [isTournamentStarted, setIsTournamentStarted] = React.useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isConfirmStartOpen, setIsConfirmStartOpen] = React.useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = React.useState(false);
  const [isTournamentEnded, setIsTournamentEnded] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDrinkOrderOpen, setIsDrinkOrderOpen] = React.useState(false);
  const [orderedDrinks, setOrderedDrinks] =
    React.useState<OrderedDrinks | null>(null);
  const [isConfirmPaymentOpen, setIsConfirmPaymentOpen] = React.useState(false);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = React.useState(false);
  const [isEventPaymentCompleted, setIsEventPaymentCompleted] =
    React.useState(false);
  const [participantPaymentFees, setParticipantPaymentFees] = React.useState<
    ParticipantPaymentFee[]
  >([]);
  const [checkedParticipants, setCheckedParticipants] = React.useState<
    Set<string>
  >(new Set());

  // モーダルが閉じたらステートをリセット
  React.useEffect(() => {
    if (!isOpen) {
      setIsTournamentStarted(false);
      setIsConfirmOpen(false);
      setIsConfirmStartOpen(false);
      setIsConfirmDeleteOpen(false);
      setIsTournamentEnded(false);
      setIsSubmitting(false);
      setIsDrinkOrderOpen(false);
      setOrderedDrinks(null);
      setIsConfirmPaymentOpen(false);
      setIsPaymentSubmitting(false);
      setIsEventPaymentCompleted(false);
      setParticipantPaymentFees([]);
      setCheckedParticipants(new Set());
    }
  }, [isOpen]);

  // モーダルが開いている間、背景のスクロールを禁止
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  // イベントが進行中の場合、ドリンク注文データを取得
  const isInProgress =
    event?.status === "in_progress" ||
    (event != null && isTournamentStarted && !isTournamentEnded);

  const fetchOrderedDrinks = React.useCallback(async () => {
    if (!event) return;
    try {
      const data = await drinksApi.getEventOrderedDrinks(event.id);
      setOrderedDrinks(data);
    } catch {
      // 取得失敗は無視
    }
  }, [event]);

  const fetchParticipantPaymentFees = React.useCallback(async () => {
    if (!event) return;
    try {
      const data = await eventsApi.getParticipantPaymentFees(event.id);
      setParticipantPaymentFees(data);
    } catch {
      // 取得失敗は無視
    }
  }, [event]);

  React.useEffect(() => {
    if (!isOpen || !event) return;
    const eventInProgress =
      event.status === "in_progress" ||
      event.status === "finished" ||
      isTournamentStarted ||
      isTournamentEnded;
    if (!eventInProgress) return;

    if (isAdmin || (event.entryStatus === "entered" && currentUserName)) {
      fetchOrderedDrinks();
    }
  }, [
    isOpen,
    event,
    isAdmin,
    isTournamentStarted,
    isTournamentEnded,
    currentUserName,
    fetchOrderedDrinks,
  ]);

  React.useEffect(() => {
    if (!isOpen || !event || !isAdmin) return;
    const eventFinished = event.status === "finished" || isTournamentEnded;
    if (!eventFinished) return;
    fetchParticipantPaymentFees();
  }, [isOpen, event, isAdmin, isTournamentEnded, fetchParticipantPaymentFees]);

  if (!isOpen || !event) return null;

  const displayFormat = event.singlesFlag
    ? UI_TEXT.eventDetail.format.singles
    : UI_TEXT.eventDetail.format.doubles;
  const displayMatchFormat = normalizeFormatLabel(event.matchFormat);
  const hasTournamentStarted =
    isTournamentStarted || event.status === "in_progress";
  const hasEventEnded = isTournamentEnded || event.status === "finished";
  const isOrWasInProgress = isInProgress || hasEventEnded;
  const isPaymentCompleted =
    isEventPaymentCompleted || Boolean(event.paymentFlg);
  const participantFeesTotal = participantPaymentFees.reduce(
    (sum, pf) => sum + pf.payment_fee,
    0,
  );
  const participantFeeDeduction = participantPaymentFees.length * 100;
  const participantFeesAdjustedTotal =
    participantFeesTotal - participantFeeDeduction;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white w-full max-w-sm rounded-[28px] overflow-hidden shadow-2xl animate-in zoom-in-95 flex flex-col max-h-[90vh] relative">
        <div className="bg-slate-900 p-6 text-white relative shrink-0 text-left">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
            aria-label={UI_TEXT.eventDetail.close}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <h3 className="text-2xl font-black tracking-tighter uppercase leading-tight mb-3 relative z-10">
            {`${event.tournament} ${event.title}`}
          </h3>
        </div>

        <div className="p-8 space-y-6 overflow-y-auto scrollbar-hide text-left">
          <div className="space-y-5">
            <InfoRow
              icon={
                <Calendar
                  className="w-5 h-5 text-slate-400 shrink-0"
                  strokeWidth={1.5}
                />
              }
              label={UI_TEXT.eventDetail.labels.date}
              value={event.date}
            />
            <InfoRow
              icon={
                <MapPin
                  className="w-5 h-5 text-slate-400 shrink-0"
                  strokeWidth={1.5}
                />
              }
              label={UI_TEXT.eventDetail.labels.location}
              value={event.location}
            />
            <div className="flex items-start gap-4">
              <Target
                className="w-5 h-5 text-slate-400 shrink-0"
                strokeWidth={1.5}
              />
              <div className="text-left">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">
                  {UI_TEXT.eventDetail.labels.format}
                </p>
                <div className="flex items-center gap-2 mt-1 mb-1">
                  <span className="font-bold text-slate-800 text-sm">
                    {displayFormat}
                  </span>
                  {event.isExciting && (
                    <span className="bg-slate-800 text-rose-400 text-[8px] font-black px-1.5 py-0.5 rounded">
                      {UI_TEXT.eventDetail.format.exciting}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 font-bold">
                  {UI_TEXT.eventDetail.labels.formatPrefix}:{" "}
                  {displayMatchFormat}
                </p>
              </div>
            </div>
            <InfoRow
              icon={
                <Banknote
                  className="w-5 h-5 text-slate-400 shrink-0"
                  strokeWidth={1.5}
                />
              }
              label={UI_TEXT.eventDetail.labels.fee}
              value={event.fee || "¥2,000"}
            />
            {event.notes && (
              <div className="flex items-start gap-4 text-left">
                <FileText
                  className="w-5 h-5 text-slate-400 shrink-0"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">
                    {UI_TEXT.eventDetail.labels.notes}
                  </p>
                  <p className="font-medium text-slate-600 text-[13px] leading-relaxed whitespace-pre-wrap">
                    {event.notes}
                  </p>
                </div>
              </div>
            )}

            {event.singlesFlag &&
              event.singlesPointFlag &&
              event.singlesRankingPoints &&
              event.singlesRankingPoints.length > 0 && (
                <div className="text-left">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                    {UI_TEXT.eventDetail.ranking.singlesTitle}
                  </p>
                  <p className="text-[12px] text-slate-500 mb-2">
                    {UI_TEXT.eventDetail.ranking.description}
                  </p>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-amber-50">
                        <th className="text-left py-1.5 px-3 text-[11px] font-bold text-amber-700 uppercase tracking-wider rounded-tl-lg">
                          {UI_TEXT.eventDetail.labels.ranking}
                        </th>
                        <th className="text-right py-1.5 px-3 text-[11px] font-bold text-amber-700 uppercase tracking-wider rounded-tr-lg">
                          {UI_TEXT.eventDetail.labels.points}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...event.singlesRankingPoints]
                        .sort(
                          (a, b) =>
                            parseInt(normalizeRank(a.rank)) -
                            parseInt(normalizeRank(b.rank)),
                        )
                        .map((rp, i) => (
                          <tr
                            key={i}
                            className="border-b border-amber-100 last:border-0"
                          >
                            <td
                              className={`py-1.5 px-3 font-semibold ${getRankAccentClass(rp.rank)}`}
                            >
                              <span className="inline-flex items-center gap-1">
                                {getRankMedal(rp.rank) && (
                                  <span>{getRankMedal(rp.rank)}</span>
                                )}
                                <span>{rp.rank}</span>
                              </span>
                            </td>
                            <td
                              className={`py-1.5 px-3 text-right font-bold ${getRankAccentClass(rp.rank)}`}
                            >
                              {rp.point}{" "}
                              {UI_TEXT.eventDetail.labels.rankPointSuffix}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

            {event.doublesFlag &&
              event.doublesPointFlag &&
              event.doublesRankingPoints &&
              event.doublesRankingPoints.length > 0 && (
                <div className="text-left">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                    {UI_TEXT.eventDetail.ranking.doublesTitle}
                  </p>
                  <p className="text-[12px] text-slate-500 mb-2">
                    {UI_TEXT.eventDetail.ranking.description}
                  </p>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-indigo-50">
                        <th className="text-left py-1.5 px-3 text-[11px] font-bold text-indigo-700 uppercase tracking-wider rounded-tl-lg">
                          {UI_TEXT.eventDetail.labels.ranking}
                        </th>
                        <th className="text-right py-1.5 px-3 text-[11px] font-bold text-indigo-700 uppercase tracking-wider rounded-tr-lg">
                          {UI_TEXT.eventDetail.labels.points}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...event.doublesRankingPoints]
                        .sort(
                          (a, b) =>
                            parseInt(normalizeRank(a.rank)) -
                            parseInt(normalizeRank(b.rank)),
                        )
                        .map((rp, i) => (
                          <tr
                            key={i}
                            className="border-b border-indigo-100 last:border-0"
                          >
                            <td
                              className={`py-1.5 px-3 font-semibold ${getRankAccentClass(rp.rank)}`}
                            >
                              <span className="inline-flex items-center gap-1">
                                {getRankMedal(rp.rank) && (
                                  <span>{getRankMedal(rp.rank)}</span>
                                )}
                                <span>{rp.rank}</span>
                              </span>
                            </td>
                            <td
                              className={`py-1.5 px-3 text-right font-bold ${getRankAccentClass(rp.rank)}`}
                            >
                              {rp.point}{" "}
                              {UI_TEXT.eventDetail.labels.rankPointSuffix}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

            <div className="text-left">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                {UI_TEXT.eventDetail.labels.participants}（
                {event.participantCount}
                {UI_TEXT.eventDetail.labels.participantUnit}）
              </p>
              {event.participants.length > 0 ? (
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-emerald-50">
                      <th className="text-left py-1.5 px-3 text-[11px] font-bold text-emerald-700 uppercase tracking-wider rounded-tl-lg">
                        No
                      </th>
                      <th className="text-left py-1.5 px-3 text-[11px] font-bold text-emerald-700 uppercase tracking-wider rounded-tr-lg">
                        {UI_TEXT.eventDetail.labels.participantName}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.participants.map((name, index) => (
                      <tr
                        key={`${name}-${index}`}
                        className="border-b border-emerald-100 last:border-0"
                      >
                        <td className="py-1.5 px-3 font-semibold text-emerald-700">
                          {index + 1}
                        </td>
                        <td className="py-1.5 px-3 font-medium text-slate-700">
                          {name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="font-medium text-slate-500 text-[13px]">
                  {UI_TEXT.eventDetail.labels.noParticipants}
                </p>
              )}
            </div>

            {/* ドリンク注文状況 */}
            {isOrWasInProgress && event.entryStatus === "entered" && (
              <div className="text-left space-y-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                  {UI_TEXT.drinkOrder.myOrderTitle}
                </p>
                {orderedDrinks &&
                  orderedDrinks.drinks.length > 0 &&
                  (() => {
                    const grouped = new Map<
                      string,
                      { name: string; price: number; count: number }
                    >();
                    for (const d of orderedDrinks.drinks) {
                      const entry = grouped.get(d.menu_id);
                      if (entry) {
                        entry.count++;
                      } else {
                        grouped.set(d.menu_id, {
                          name: d.menu_name,
                          price: d.price,
                          count: 1,
                        });
                      }
                    }
                    return (
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-sky-50">
                            <th className="text-left py-1.5 px-3 text-[11px] font-bold text-sky-700 uppercase tracking-wider rounded-tl-lg">
                              ドリンク
                            </th>
                            <th className="text-right py-1.5 px-3 text-[11px] font-bold text-sky-700 uppercase tracking-wider rounded-tr-lg">
                              数量
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from(grouped.values()).map((item, i) => (
                            <tr
                              key={i}
                              className="border-b border-sky-100 last:border-0"
                            >
                              <td className="py-1.5 px-3 font-medium text-slate-700">
                                {item.name}
                                <span className="ml-1 text-slate-400 text-xs">
                                  ¥{item.price.toLocaleString()}
                                </span>
                              </td>
                              <td className="py-1.5 px-3 text-right font-semibold text-slate-700">
                                {item.count}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                <div className="bg-slate-50 rounded-lg px-3 py-2 space-y-1">
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{UI_TEXT.drinkOrder.baseFee}</span>
                    <span>
                      ¥{(orderedDrinks?.basic_fee ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{UI_TEXT.drinkOrder.drinkFee}</span>
                    <span>
                      ¥
                      {orderedDrinks
                        ? (
                            orderedDrinks.total_amount - orderedDrinks.basic_fee
                          ).toLocaleString()
                        : "0"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-bold text-slate-800 pt-1 border-t border-slate-200">
                    <span>{UI_TEXT.drinkOrder.totalFee}</span>
                    <span>
                      ¥{(orderedDrinks?.total_amount ?? 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* 管理者: 参加費管理 */}
            {isAdmin && hasEventEnded && participantPaymentFees.length > 0 && (
              <div className="text-left space-y-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                  {UI_TEXT.drinkOrder.participantFeesTitle}
                </p>
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-amber-50">
                      {!isPaymentCompleted && (
                        <th className="py-1.5 px-3 text-[11px] font-bold text-amber-700 uppercase tracking-wider rounded-tl-lg w-8" />
                      )}
                      <th
                        className={`text-left py-1.5 px-3 text-[11px] font-bold text-amber-700 uppercase tracking-wider ${isPaymentCompleted ? "rounded-tl-lg" : ""}`}
                      >
                        氏名
                      </th>
                      <th className="text-right py-1.5 px-3 text-[11px] font-bold text-amber-700 uppercase tracking-wider rounded-tr-lg">
                        料金
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {participantPaymentFees.map((pf, i) => (
                      <tr
                        key={i}
                        className={`border-b border-amber-100 last:border-0 ${pf.payment_fee >= 10000 ? "bg-rose-50" : ""}`}
                      >
                        {!isPaymentCompleted && (
                          <td className="py-1.5 px-3">
                            <input
                              type="checkbox"
                              checked={checkedParticipants.has(
                                pf.participant_name,
                              )}
                              onChange={(e) => {
                                setCheckedParticipants((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked)
                                    next.add(pf.participant_name);
                                  else next.delete(pf.participant_name);
                                  return next;
                                });
                              }}
                              className="w-4 h-4 accent-emerald-600"
                            />
                          </td>
                        )}
                        <td
                          className={`py-1.5 px-3 font-medium text-slate-700 ${
                            !isPaymentCompleted &&
                            checkedParticipants.has(pf.participant_name)
                              ? "line-through text-slate-400"
                              : ""
                          }`}
                        >
                          {pf.participant_name}
                        </td>
                        <td className="py-1.5 px-3 text-right font-semibold text-slate-700">
                          ¥{pf.payment_fee.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-between text-sm font-bold text-slate-800 bg-amber-50 rounded-lg px-3 py-2">
                  <span>{UI_TEXT.drinkOrder.participantFeesTotal}</span>
                  <span>¥{participantFeesTotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-baseline text-sm font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <span>{UI_TEXT.drinkOrder.participantFeesAdjustedTotal}</span>
                  <span className="text-right">
                    ¥{participantFeesAdjustedTotal.toLocaleString()}
                    <span className="ml-1 text-xs font-semibold text-slate-400">
                      (¥{participantFeeDeduction.toLocaleString()})
                    </span>
                  </span>
                </div>
                {isPaymentCompleted ? (
                  <div className="flex items-center justify-center gap-2 py-2 text-emerald-600 font-bold text-sm">
                    <span>✓</span>
                    <span>{UI_TEXT.drinkOrder.paymentCompleted}</span>
                  </div>
                ) : (
                  <button
                    className="w-full py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm tracking-wide active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={isPaymentSubmitting}
                    onClick={() => setIsConfirmPaymentOpen(true)}
                  >
                    {UI_TEXT.drinkOrder.paymentCompleteButton}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3 pt-4">
            {hasEventEnded ? (
              <>
                <p className="w-full text-center py-5 rounded-xl bg-slate-100 text-slate-500 font-bold tracking-wide">
                  {UI_TEXT.eventDetail.finished}
                </p>
                {onViewTournamentResult && (
                  <button
                    className="w-full bg-slate-900 text-white py-4 rounded-lg font-bold uppercase tracking-widest shadow-md active:scale-95 transition-all"
                    onClick={() => onViewTournamentResult(event)}
                  >
                    {UI_TEXT.eventDetail.actions.showResult}
                  </button>
                )}
                {event.entryStatus === "entered" &&
                  currentUserName &&
                  !isPaymentCompleted && (
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-xl active:scale-95 transition-all hover:bg-amber-100"
                      onClick={() => setIsDrinkOrderOpen(true)}
                    >
                      <Martini className="w-6 h-6 text-amber-500" />
                      <span className="flex-1 text-left font-bold text-amber-800 text-sm tracking-wide">
                        {UI_TEXT.drinkOrder.orderButton}
                      </span>
                      <span className="text-amber-400 text-lg">›</span>
                    </button>
                  )}
              </>
            ) : hasTournamentStarted ? (
              <>
                <button
                  className="w-full bg-amber-100 text-amber-500 py-5 rounded-xl font-black uppercase tracking-widest shadow-xl cursor-not-allowed"
                  disabled
                >
                  {UI_TEXT.eventDetail.actions.entryClosed}
                </button>
                {/* 参加済みユーザーにドリンク注文バナーを表示 */}
                {event.entryStatus === "entered" &&
                  currentUserName &&
                  !isPaymentCompleted && (
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-xl active:scale-95 transition-all hover:bg-amber-100"
                      onClick={() => setIsDrinkOrderOpen(true)}
                    >
                      <Martini className="w-6 h-6 text-amber-500" />
                      <span className="flex-1 text-left font-bold text-amber-800 text-sm tracking-wide">
                        {UI_TEXT.drinkOrder.orderButton}
                      </span>
                      <span className="text-amber-400 text-lg">›</span>
                    </button>
                  )}
              </>
            ) : event.entryStatus === "entered" ? (
              <>
                <button
                  className="w-full bg-slate-200 text-slate-400 py-5 rounded-xl font-black uppercase tracking-widest shadow-sm cursor-not-allowed"
                  disabled
                >
                  {UI_TEXT.eventDetail.actions.entered}
                </button>
                <button
                  className="w-full text-slate-400 font-bold text-[11px] uppercase tracking-[0.2em] py-1 text-center transition-colors hover:text-rose-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                  onClick={async () => {
                    if (!onCancelEntry) return;
                    setIsSubmitting(true);
                    await onCancelEntry(event.id);
                    onClose();
                  }}
                >
                  {isSubmitting
                    ? UI_TEXT.eventDetail.actions.submitting
                    : UI_TEXT.eventDetail.actions.cancelEntry}
                </button>
              </>
            ) : (
              <button
                className="w-full bg-slate-900 text-white py-5 rounded-xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
                disabled={isSubmitting}
                onClick={async () => {
                  if (!onEntry) return;
                  setIsSubmitting(true);
                  await onEntry(event.id);
                  onClose();
                }}
              >
                {isSubmitting
                  ? UI_TEXT.eventDetail.actions.submitting
                  : UI_TEXT.eventDetail.actions.enter}
              </button>
            )}
            {isAdmin && !hasEventEnded && (
              <>
                {!hasTournamentStarted ? (
                  <button
                    className="w-full bg-amber-600 text-white py-4 rounded-lg font-bold uppercase tracking-widest shadow-md active:scale-95 transition-all mt-2"
                    onClick={() => setIsConfirmStartOpen(true)}
                  >
                    {UI_TEXT.eventDetail.actions.startTournament}
                  </button>
                ) : (
                  <button
                    className={`w-full py-4 rounded-lg font-bold uppercase tracking-widest shadow-md transition-all mt-2 ${
                      hasEventEnded
                        ? "bg-slate-300 text-slate-400 cursor-not-allowed"
                        : "bg-rose-600 text-white active:scale-95"
                    }`}
                    onClick={() => !hasEventEnded && setIsConfirmOpen(true)}
                    disabled={hasEventEnded}
                  >
                    {UI_TEXT.eventDetail.actions.endEvent}
                  </button>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={onEdit}
                    className="flex-1 bg-white border border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[10px] py-3.5 rounded-lg flex items-center justify-center gap-2 shadow-sm hover:bg-slate-50 transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />{" "}
                    {UI_TEXT.eventDetail.actions.edit}
                  </button>
                  <button
                    onClick={() => setIsConfirmDeleteOpen(true)}
                    className="flex-1 bg-white border border-rose-200 text-rose-600 font-bold uppercase tracking-wider text-[10px] py-3.5 rounded-lg flex items-center justify-center gap-2 shadow-sm hover:bg-rose-50 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />{" "}
                    {UI_TEXT.eventDetail.actions.delete}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* イベント終了確認ダイアログ */}
        {isConfirmOpen && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm rounded-[28px]">
            <div className="bg-white rounded-2xl p-8 mx-6 shadow-2xl animate-in zoom-in-95 duration-200">
              <h4 className="font-black text-slate-800 text-lg tracking-tight mb-2">
                {UI_TEXT.eventDetail.confirmEnd.title}
              </h4>
              <p className="text-slate-400 text-sm mb-6">
                {UI_TEXT.eventDetail.confirmEnd.description}
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 py-3 rounded-lg border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors"
                  onClick={() => setIsConfirmOpen(false)}
                >
                  {UI_TEXT.eventDetail.confirmEnd.cancel}
                </button>
                <button
                  className="flex-1 py-3 rounded-lg bg-rose-600 text-white font-bold text-sm active:scale-95 transition-all"
                  onClick={() => {
                    setIsConfirmOpen(false);
                    setIsTournamentEnded(true);
                    if (onEndTournament && event) onEndTournament(event.id);
                  }}
                >
                  {UI_TEXT.eventDetail.confirmEnd.confirm}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* イベント開始確認ダイアログ */}
        {isConfirmStartOpen && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm rounded-[28px]">
            <div className="bg-white rounded-2xl p-8 mx-6 shadow-2xl animate-in zoom-in-95 duration-200">
              <h4 className="font-black text-slate-800 text-lg tracking-tight mb-2">
                {UI_TEXT.eventDetail.confirmStart.title}
              </h4>
              <p className="text-slate-400 text-sm mb-6">
                {UI_TEXT.eventDetail.confirmStart.description}
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 py-3 rounded-lg border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors"
                  onClick={() => setIsConfirmStartOpen(false)}
                >
                  {UI_TEXT.eventDetail.confirmStart.cancel}
                </button>
                <button
                  className="flex-1 py-3 rounded-lg bg-amber-600 text-white font-bold text-sm active:scale-95 transition-all"
                  onClick={async () => {
                    setIsConfirmStartOpen(false);
                    if (!onStartTournament) return;
                    const ok = await onStartTournament(event.id);
                    if (ok) setIsTournamentStarted(true);
                  }}
                >
                  {UI_TEXT.eventDetail.confirmStart.confirm}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* イベント削除確認ダイアログ */}
        {isConfirmDeleteOpen && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm rounded-[28px]">
            <div className="bg-white rounded-2xl p-8 mx-6 shadow-2xl animate-in zoom-in-95 duration-200">
              <h4 className="font-black text-slate-800 text-lg tracking-tight mb-2">
                {UI_TEXT.eventDetail.confirmDelete.title}
              </h4>
              <p className="text-slate-400 text-sm mb-6">
                {UI_TEXT.eventDetail.confirmDelete.description}
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 py-3 rounded-lg border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors"
                  onClick={() => setIsConfirmDeleteOpen(false)}
                >
                  {UI_TEXT.eventDetail.confirmDelete.cancel}
                </button>
                <button
                  className="flex-1 py-3 rounded-lg bg-rose-600 text-white font-bold text-sm active:scale-95 transition-all"
                  onClick={async () => {
                    setIsConfirmDeleteOpen(false);
                    if (!onDelete) return;
                    await onDelete(event.id);
                  }}
                >
                  {UI_TEXT.eventDetail.confirmDelete.confirm}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ドリンク注文モーダル */}
        {isDrinkOrderOpen && currentUserName && (
          <DrinkOrderModal
            isOpen={isDrinkOrderOpen}
            eventId={event.id}
            currentUserName={currentUserName}
            onClose={() => setIsDrinkOrderOpen(false)}
            onOrderSuccess={() => {
              setIsDrinkOrderOpen(false);
              fetchOrderedDrinks();
            }}
          />
        )}

        {/* 支払い完了確認ダイアログ */}
        {isConfirmPaymentOpen && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm rounded-[28px]">
            <div className="bg-white rounded-2xl p-8 mx-6 shadow-2xl animate-in zoom-in-95 duration-200">
              <h4 className="font-black text-slate-800 text-lg tracking-tight mb-2">
                {UI_TEXT.drinkOrder.confirmPayment.title}
              </h4>
              <p className="text-slate-400 text-sm mb-6">
                {UI_TEXT.drinkOrder.confirmPayment.description}
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 py-3 rounded-lg border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors"
                  onClick={() => setIsConfirmPaymentOpen(false)}
                  disabled={isPaymentSubmitting}
                >
                  {UI_TEXT.drinkOrder.confirmPayment.cancel}
                </button>
                <button
                  className="flex-1 py-3 rounded-lg bg-emerald-600 text-white font-bold text-sm active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isPaymentSubmitting}
                  onClick={async () => {
                    setIsPaymentSubmitting(true);
                    try {
                      await drinksApi.completeEventDrinkPayment(event.id);
                      setIsEventPaymentCompleted(true);
                    } finally {
                      setIsPaymentSubmitting(false);
                      setIsConfirmPaymentOpen(false);
                    }
                  }}
                >
                  {isPaymentSubmitting
                    ? UI_TEXT.drinkOrder.submitting
                    : UI_TEXT.drinkOrder.confirmPayment.confirm}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-4">
    {icon}
    <div className="text-left">
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">
        {label}
      </p>
      <p className="font-bold text-slate-800 text-sm">{value}</p>
    </div>
  </div>
);

export default EventDetailModal;
